import { Component, Input, ViewEncapsulation, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { trigger, style, animate, transition } from "@angular/animations";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { environment } from "../../../environments/environment";
import { AreaChartComponent, AreaChartSeries, ChartCommonModule, AxesModule, AreaChartModule, LineChartModule } from "@swimlane/ngx-charts";
import { KilowantColorHelper } from "./KilowantColorHelper"
import { DateTime } from "luxon";

@Component({
    selector: "kilowant-charts-home-price-history",
    templateUrl: "../../../html/chart/kilowant-charts-home-price-history.html",
    styleUrls: ["../../../css/base-chart.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("animationState", [
            transition(":leave", [
                style({
                    opacity: 1,
                    transform: '*'
                }),
                animate(500, style({ opacity: 0, transform: "scale(0)" }))
            ])
        ])
    ],
    imports: [ChartCommonModule, AxesModule, AreaChartModule, LineChartModule]
})

export class KilowantAreaPriceHistoryChartComponent extends AreaChartComponent {
    @Input() pointDate: Date | null = null;
    @Input() pointValue: number | null = null;

    colorHelper: KilowantColorHelper;
    colorsForLine: any;
    cx: number;
    cy: number;

    public useMonthlyDateFormat: boolean = false;

    ngOnInit() {
        this.colorHelper = new KilowantColorHelper(this.colors, this.scheme, this.schemeType, ["Price History"], ["#" + environment.RI_UTILITY_COLOR]);
        this.wrapTicks = true;
        this.roundDomains = false;
        this.useMonthlyDateFormat = false;
        this.cx = 100;
        this.cy = 100;
    }

    update(): void {
        super.update();

        this.yAxisTicks = [];
        this.xAxisTicks = [];

        this.yScaleMin = null!;
        var step = (this.yDomain[1] - this.yDomain[0]) / 9;

        for (var i = 0; i <= 9; i++) {
            this.yAxisTicks.push(this.yDomain[0] + i * step);
        }

        var startTS = this.xDomain[0].getTime() / 1000;
        var endTS = this.xDomain[1].getTime() / 1000;

        var timeDiff = (endTS - startTS);
        if (timeDiff > 10368000) {
            this.useMonthlyDateFormat = true;
        } else {
            this.useMonthlyDateFormat = false;
        }

        var step = timeDiff / 5;
        if (!isNaN(step)) {
            for (var i = 1; i < 6; i++) {
                var d = new Date(0);
                d.setUTCSeconds(this.xDomain[0].getTime() / 1000 + i * step - step / 2);
                this.xAxisTicks.push(d);
            }
        }
        if (this.pointDate != null && this.pointValue != null) {
            var pos = 0;
            var pointD = DateTime.fromJSDate(this.pointDate).startOf("day");
            for (let i = 0; i < this.results[0].series.length; i++) {
                if (DateTime.fromJSDate(this.results[0].series[i].name).startOf("day") < pointD) {
                    continue;
                }
                if (DateTime.fromJSDate(this.results[0].series[i].name).startOf("day") >= pointD) {
                    pos = i;
                    break;
                }
            }
            var totalPeriodDays = DateTime.fromJSDate(this.results[0].series[this.results[0].series.length - 1].name).startOf("day").diff(DateTime.fromJSDate(this.results[0].series[0].name).startOf("day")).as("days");
            var posDays = DateTime.fromJSDate(this.results[0].series[pos].name).startOf("day").diff(DateTime.fromJSDate(this.results[0].series[0].name).startOf("day")).as("days");

            this.cx = this.dims.width * posDays / totalPeriodDays;
            this.cy = this.dims.height - (this.dims.height / (this.yDomain[1] - this.yDomain[0]) * (this.pointValue - this.yDomain[0]));
        }
    }

    updateDomain(domain: any): void {
        this.filteredDomain = domain;
        this.xDomain = this.filteredDomain;
        this.xScale = this.getXScale(this.xDomain, this.dims.width);
    }

    getYDomain(): [number, number] {
        const domain: any[] = [];

        for (const results of this.results) {
            for (const d of results.series) {
                if (!domain.includes(d.value)) {
                    domain.push(d.value);
                }
            }
        }

        const values = [...domain];
        values.push(this.pointValue!);
        const min = Math.min(...values) * 0.98;
        const max = Math.max(...values) * 1.001;
        return [min, max];
    }

    yAxisFormat(val: number): string | null {
        return (new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3");
    }

    xAxisFormatDaily(val: Date): string | null {
        return (new DatePipe("en-US")).transform(val, "M/d/yyyy");
    }

    xAxisFormatMonthly(val: Date): string | null {
        return (new DatePipe("en-US")).transform(val, "M/yyyy");
    }

    dataApply(i: any): AreaChartSeries {
        return <AreaChartSeries>i;
    }
}
